import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Grid, Hidden } from '@material-ui/core';

import { GlobalContext } from '../../../../store';
import { IAdminGroup } from '../../../../store/admin/groups/store';
import { IAdminMembership } from '../../../../store/admin/memberships/store';

import AddPlayersCard from '../../../../components/AddPlayersCard';
import Tooltip from '../../../../components/Tooltip';
import { EventWizardStepTitle } from '../Components/EventWizardStepTitle/EventWizardStepTitle';
import GroupInvitations from './Components/GroupInvitations/GroupInvitations';
import { getSelectedGroups } from './Utils/getSelectedGroups';
import { getSelectedMembers } from './Utils/getSelectedMembers';
import { selectAllGroups } from './Utils/selectAllGroups';
import { UniqueLinkToEventContainer } from './Components/UniqueLinkToEventContainer';
import { sortBy } from '../../../../contrib/sortBy';
import EventPlayersField from './Components/EventPlayersField/EventPlayersField';
import { useBoolean } from '../../../common/Hooks/useBoolean.hook';
import InvitePlayersConfirmationModal from './Components/InvitePlayersConfirmationModal';

export interface ISelectedGroups {
  [-1]: boolean;
  [-2]: boolean;
  [-3]: boolean;
  [groupId: number]: boolean;
}

interface IProps {
  clubId: string;
  eventId: string;
}

export const allGroupId = -3;
const activeGroupId = -2;
const guestGroupId = -1;

interface IBaseGroup {
  club: number;
  members: any[];
}

interface IGroups {
  active: IAdminGroup;
  all: IAdminGroup;
  guest: IAdminGroup;
}

const InvitePlayers = (props: IProps) => {
  const { clubId, eventId } = props;

  const {
    actions: {
      adminMemberships: { getMemberships },
      adminGroups: { getGroups },
      adminEventEnrollments: { sendMembersInvitations }
    },
    adminMemberships: { memberships },
    adminGroups: { groups }
  } = useContext(GlobalContext);

  const [selectedMembers, setSelectedMembers] = useState<IAdminMembership[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<ISelectedGroups>({
    [activeGroupId]: false,
    [allGroupId]: false,
    [guestGroupId]: false
  });
  const [defaultGroup, setDefaultGroup] = useState<IBaseGroup>();
  const [defaultGroups, setDefaultGroups] = useState<IGroups>();

  const [invitationGroups, setInvitationGroups] = useState<IAdminGroup[]>([]);

  const [confirmationModalIsOpen, , toggleConfirmationModalIsOpen] = useBoolean(false);

  useEffect(() => {
    setDefaultGroup({
      club: Number(clubId),
      members: []
    });
  }, [clubId]);

  useEffect(() => {
    if (defaultGroup) {
      setDefaultGroups({
        active: {
          ...defaultGroup,
          description: 'Active members',
          id: activeGroupId,
          name: 'Active members'
        },
        all: { ...defaultGroup, description: 'All Players', id: allGroupId, name: 'All' },
        guest: {
          ...defaultGroup,
          description: 'Guest members',
          id: guestGroupId,
          name: 'Guest members'
        }
      });
    }
  }, [defaultGroup]);

  useEffect(() => {
    if (clubId) {
      getMemberships(Number(clubId), true).catch();
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId) {
      getGroups(Number(clubId)).catch();
    }
  }, [clubId]);

  useEffect(() => {
    if (Object.keys(selectedGroups).length === 3 && groups.length > 0) {
      setSelectedGroups(selectAllGroups(selectedGroups, groups));
    }
  }, [selectedGroups, groups.length]);

  useEffect(() => {
    setSelectedMembers(
      getSelectedMembers(
        memberships,
        selectedGroups,
        selectedGroups[activeGroupId],
        selectedGroups[guestGroupId]
      )
    );
  }, [memberships.length, selectedGroups, activeGroupId, guestGroupId]);

  useEffect(() => {
    setInvitationGroups(
      sortBy([...groups, ...Object.values(defaultGroups ? defaultGroups : {})], 'asc', 'id')
    );
  }, [groups.length, defaultGroups]);

  const onInvitationsSubmit = useCallback(async (): Promise<any> => {
    if (selectedMembers.length) {
      const selectedMembersId = selectedMembers.map((m: IAdminMembership) => m.user.id);

      await sendMembersInvitations(Number(eventId), selectedMembersId);

      toggleConfirmationModalIsOpen();
    }
  }, [eventId, selectedMembers]);

  const onGroupSetup = useCallback(
    (selectedGroup: IAdminGroup) => async () => {
      await setSelectedGroups({
        ...selectedGroups,
        ...getSelectedGroups(selectedGroups, selectedGroup)
      });
    },
    [selectedGroups]
  );

  return (
    <div className="event-wizard-page form">
      <div className="event-player-field">
        <EventWizardStepTitle
          buttonText="SEND INVITATIONS"
          buttonOnClick={onInvitationsSubmit}
          title="INVITE PLAYERS"
        />

        <div className="player-field-desc">
          Once an event is published, Players are now able to register. You can invite players from
          your club from this screen. When you send an invitation players will get an email
          notifying them of a new event. If an invitee is already a WAGL user the event will show up
          in the event feed of that player.
        </div>

        <Grid className="grid content" container={true} spacing={4}>
          <Grid className="grid-item left" item={true} xs={12} md={4}>
            <Hidden smDown={true}>
              <div className="title__container override">
                <div className="bot-16">INVITE PLAYERS TO THIS EVENT</div>

                <Tooltip sectionKey="INVITE_PLAYERS.INVITE_TO_EVENT" />
              </div>
            </Hidden>

            <GroupInvitations
              eventId={Number(eventId)}
              groupSetup={onGroupSetup}
              groups={invitationGroups}
              selectedGroups={selectedGroups || {}}
            />

            <AddPlayersCard
              buttonText={'Add Players'}
              className={'add-emails-full accordion-card'}
              descriptionText={`Add new guest players by email. You can add more than one
                  player by separating each email address by a comma. A guest
                  that has played in an event before should be available through
                  “find a player”.`}
              headerText={'ADD PLAYERS BY EMAIL'}
              isGuest={true}
            />

            <Hidden smDown={true}>
              <UniqueLinkToEventContainer eventId={eventId} />
            </Hidden>
          </Grid>

          <Grid className="grid-item left" item={true} xs={12} md={8}>
            <EventPlayersField
              clubId={clubId}
              eventId={eventId}
              selectedMembers={selectedMembers}
              setSelectedMembers={setSelectedMembers}
              onInvitationsSubmit={onInvitationsSubmit}
            />
          </Grid>
        </Grid>

        <InvitePlayersConfirmationModal
          isOpen={confirmationModalIsOpen}
          toggleOpen={toggleConfirmationModalIsOpen}
        />
      </div>
    </div>
  );
};

export { InvitePlayers };
